import { useCallback, useState } from 'react'
import { apiFetch } from '../../api/core'
import type { LineGenerationConfig, PriceListSummary, TdLine } from '../../types/testdata.types'

interface GenerateRandomLinesArgs {
  lines: TdLine[]
  roundUnitPrices: boolean
  roundUnitPriceValue: number
  vatOverrides: Array<{ code: string; percentage: number }>
  setLines: React.Dispatch<React.SetStateAction<TdLine[]>>
  // REQ-TESTDATA-004 (v0.8.1) — when `lineGeneration.mode === 'precision_test'`,
  // materialize the canonical precision-test preview instead of the ordinary
  // catalogue random-lines fetch (same endpoint, extended with optional
  // precision query params — see `get_random_lines` backend docstring).
  lineGeneration?: LineGenerationConfig
  onMaterialized?: () => void
}

/** Prijslijsten + het ophalen van willekeurige artikelregels daaruit. Neemt
 * de huidige regels/BTW-overrides/ronde-stukprijs-instelling als argument
 * (die state hoort bij `useLinesState`/`useVatOverrides`) i.p.v. ze zelf bij
 * te houden. */
export function usePriceListsAndRandomLines() {
  const [availablePriceLists, setAvailablePriceLists] = useState<PriceListSummary[]>([])
  const [randomPriceListId, setRandomPriceListId] = useState<number | null>(null)
  const [randomCount, setRandomCount] = useState(5)
  const [randomLoading, setRandomLoading] = useState(false)
  const [randomError, setRandomError] = useState<string | null>(null)

  const loadPriceLists = useCallback(async () => {
    try {
      const data = await apiFetch<PriceListSummary[]>('/api/pricelists')
      setAvailablePriceLists(data)
      if (data.length > 0) setRandomPriceListId(data[0].id)
    } catch { /* niet kritisch */ }
  }, [])

  const generateRandomLines = async ({
    lines, roundUnitPrices, roundUnitPriceValue, vatOverrides, setLines, lineGeneration, onMaterialized,
  }: GenerateRandomLinesArgs) => {
    setRandomError(null)
    if (!randomPriceListId) { setRandomError('Selecteer een prijslijst.'); return }
    const isPrecision = lineGeneration?.mode === 'precision_test'
    setRandomLoading(true)
    try {
      const count = isPrecision ? (lineGeneration!.line_count ?? randomCount) : randomCount
      const params = new URLSearchParams({
        price_list_id: String(randomPriceListId),
        count: String(count),
      })
      vatOverrides.forEach(v => {
        params.append('vat_codes', v.code)
        params.append('vat_percentages', String(v.percentage))
      })
      // REQ-TESTDATA-004: materialize the SAME canonical precision transform
      // final generation uses (`build_precision_lines()` server-side) — the
      // frontend never reimplements the precision algorithm itself.
      if (isPrecision && lineGeneration) {
        params.set('precision_mode', 'true')
        params.set('quantity_mode', lineGeneration.quantity_mode)
        params.set('quantity_precision_min', String(lineGeneration.quantity_precision_min))
        params.set('quantity_precision_max', String(lineGeneration.quantity_precision_max))
        params.set('unit_price_precision_min', String(lineGeneration.unit_price_precision_min))
        params.set('unit_price_precision_max', String(lineGeneration.unit_price_precision_max))
        params.set('vary_precision_per_line', String(lineGeneration.vary_precision_per_line))
      }
      const fetchedLines = await apiFetch<TdLine[]>(`/api/testdata/random-lines?${params}`)
      // Ronde-stukprijzen-instelling geldt ook voor vers opgehaalde willekeurige regels —
      // anders zou de prijslijst-prijs alsnog het afrondingsprobleem terugbrengen. Niet van
      // toepassing in precision mode: een vaste ronde prijs zou de precisie-variatie tenietdoen.
      const newLines = (!isPrecision && roundUnitPrices)
        ? fetchedLines.map(l => ({ ...l, unit_price: roundUnitPriceValue }))
        : fetchedLines
      if (lines.length > 1 || lines[0].article_number.trim() !== '') {
        const ok = window.confirm(
          isPrecision
            ? `Er zijn al ${lines.length} regel(s) ingevuld. Vervangen door ${newLines.length} precisietestregels?`
            : `Er zijn al ${lines.length} regel(s) ingevuld. Vervangen door ${newLines.length} willekeurige regels?`
        )
        if (!ok) return
      }
      setLines(newLines)
      onMaterialized?.()
    } catch (e: unknown) {
      setRandomError(e instanceof Error ? e.message : 'Ophalen mislukt')
    } finally {
      setRandomLoading(false)
    }
  }

  return {
    availablePriceLists,
    randomPriceListId, setRandomPriceListId,
    randomCount, setRandomCount,
    randomLoading, randomError,
    loadPriceLists, generateRandomLines,
  }
}

export type PriceListsAndRandomLinesState = ReturnType<typeof usePriceListsAndRandomLines>
